/**
 * A failed API call.
 *
 * `status` is the field `query-client.ts`'s retry predicate reads to stop
 * retrying 4xx, so it has to survive whatever threw. `errorCode` is the API's
 * own vocabulary — every handler returns `{ success: false, errorCode }` — and
 * is what a caller should branch on rather than parsing `message`.
 */
export class ApiError extends Error {
  readonly status: number
  readonly errorCode?: string
  readonly body?: unknown

  constructor(
    message: string,
    options: { status: number; errorCode?: string; body?: unknown },
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status
    this.errorCode = options.errorCode
    this.body = options.body
  }

  /** The session is gone or was never valid — the caller should re-authenticate. */
  get isUnauthenticated() {
    return this.status === 401
  }

  /**
   * The API returns 403 for BOTH a dead session and a genuine permission
   * failure, so this cannot be treated as "sign the user out" on its own.
   * See the same ambiguity called out in `constellation-sdk`'s executeRequest.
   */
  get isForbidden() {
    return this.status === 403
  }
}

/** A GraphQL response that came back 200 but carried `errors`. */
export class GraphqlError extends ApiError {
  constructor(message: string, body: unknown) {
    super(message, { status: 200, body })
    this.name = 'GraphqlError'
  }
}
